import { ArrowLeftOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, message, Modal, Row, Select, Space, Switch, Table, Tag, Typography } from "antd";
import dayjs from 'dayjs';
import { debounce } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from "../../../../Contexts/UserContext";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";
import { checkOverlappingDates, checkTransfersOutsideDateRange, createRule, getAffectedSubmissions, getAgencies, getAgents, getRuleById, getTransfersByRuleId, updateRule } from "./psrlTransferRulesService";
import TransfersTable from "./TransfersTable";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PSRLRuleForm = () => {
  const { auth } = useContext(UserContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  // const [agents, setAgents] = useState([]); // Will be used when implementing agent selection
  const [searchingAgencies, setSearchingAgencies] = useState(false);
  const [searchingAgents, setSearchingAgents] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [originalValues, setOriginalValues] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [affectedSubmissions, setAffectedSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const loadRule = useCallback(async () => {
    try {
      setLoading(true);
      const rule = await getRuleById(parseInt(id));
      if (rule) {
        // Check if rule is reversed and redirect if so
        if (rule.PSRL_Is_Reversed) {
          message.error("Cannot edit a reversed rule");
          navigate("/settings?tab=3&subtab=2");
          return;
        }
        
        const formValues = {
          PSRL_TransferType: rule.PSRL_TransferType,
          PSRL_ForAgency_ACAY_KeyID: rule.PSRL_ForAgency_ACAY_KeyID,
          PSRL_TransferToAgent_ACAG_KeyID: rule.PSRL_TransferToAgent_ACAG_KeyID,
          PSRL_Rule_From_Date: rule.PSRL_Rule_From_Date ? dayjs(rule.PSRL_Rule_From_Date) : null,
          PSRL_Rule_To_Date: rule.PSRL_Rule_To_Date ? dayjs(rule.PSRL_Rule_To_Date) : null,
          PSRL_Active: rule.PSRL_Active,
          PSRL_Rule_Name: rule.PSRL_Rule_Name || '',
          PSRL_Note: rule.PSRL_Note || '',
          PSRL_TransferPointsPercentage: rule.PSRL_TransferPointsPercentage || null
        };
        
        form.setFieldsValue(formValues);
        setOriginalValues(formValues);
        setHasChanges(false);
        setSelectedAgency(rule.PSRL_ForAgency_ACAY_KeyID);
        if (rule.PSRL_ForAgency_ACAY_KeyID) {
          loadAgentsForAgency(rule.PSRL_ForAgency_ACAY_KeyID);
        }
      }
    } catch (err) {
      message.error(err.message || "Failed to load rule");
      navigate("/settings?tab=3&subtab=2");
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate]);

  const loadTransfers = useCallback(async () => {
    if (!isEdit || !id) return;
    
    try {
      setTransfersLoading(true);
      const transfersData = await getTransfersByRuleId(parseInt(id));
      setTransfers(transfersData || []);
    } catch (err) {
      console.error("Failed to load transfers:", err);
      message.error("Failed to load transfers for this rule");
      setTransfers([]);
    } finally {
      setTransfersLoading(false);
    }
  }, [id, isEdit]);

  const loadAffectedSubmissions = useCallback(async () => {
    if (!isEdit || !id) return;
    
    try {
      setSubmissionsLoading(true);
      const submissionsData = await getAffectedSubmissions(parseInt(id));
      setAffectedSubmissions(submissionsData || []);
    } catch (err) {
      console.error("Failed to load affected submissions:", err);
      message.error("Failed to load affected submissions for this rule");
      setAffectedSubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    // Only make API calls if we have a valid auth state
    if (auth?.token) {
      // Load initial small set of agencies (first 50 alphabetically)
      loadAgencies("A");
      if (isEdit) {
        loadRule();
        loadTransfers();
        loadAffectedSubmissions();
      }
    }
  }, [auth?.token, isEdit, id, loadRule, loadTransfers, loadAffectedSubmissions]);

  // Handle browser navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isEdit && hasChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEdit, hasChanges]);

  const loadAgencies = async (searchTerm = "") => {
    try {
      setSearchingAgencies(true);
      const agencyList = await getAgencies(searchTerm);
      setAgencies(Array.isArray(agencyList) ? agencyList : []);
    } catch (err) {
      message.error("Failed to load agencies");
      setAgencies([]);
    } finally {
      setSearchingAgencies(false);
    }
  };

  // Debounced search for agencies
  const debouncedAgencySearch = useMemo(
    () => debounce((searchTerm) => {
      loadAgencies(searchTerm);
    }, 300),
    []
  );

  // Debounced search for agents
  const debouncedAgentSearch = useMemo(
    () => debounce((searchTerm) => {
      if (selectedAgency) {
        loadAgentsForAgency(selectedAgency, searchTerm);
      }
    }, 300),
    [selectedAgency]
  );

  const loadAgentsForAgency = async (agencyId, searchTerm = "") => {
    try {
      setSearchingAgents(true);
      // Convert to string to avoid JavaScript number precision issues
      const agentList = await getAgents(searchTerm, agencyId.toString());
      console.log('Agents loaded for agency:', agencyId, agentList);
      
      setFilteredAgents(agentList);
      // setAgents(agentList); // Will be used when implementing agent selection
    } catch (err) {
      console.error('Error loading agents:', err);
      message.error("Failed to load agents");
      setFilteredAgents([]);
      // setAgents([]); // Will be used when implementing agent selection
    } finally {
      setSearchingAgents(false);
    }
  };

  const handleAgencyChange = (agencyId) => {
    setSelectedAgency(agencyId);
    form.setFieldsValue({ PSRL_TransferToAgent_ACAG_KeyID: undefined });
    if (agencyId) {
      loadAgentsForAgency(agencyId);
    } else {
      setFilteredAgents([]);
    }
  };

  const checkForChanges = (currentValues) => {
    if (!originalValues || !isEdit) return false;
    
    // Compare each field
    const fieldsToCompare = [
      'PSRL_TransferType',
      'PSRL_ForAgency_ACAY_KeyID', 
      'PSRL_TransferToAgent_ACAG_KeyID',
      'PSRL_Active'
    ];
    
    for (const field of fieldsToCompare) {
      if (originalValues[field] !== currentValues[field]) {
        return true;
      }
    }
    
    // Compare dates (handle dayjs objects)
    const fromDateChanged = !dayjs(originalValues.PSRL_Rule_From_Date).isSame(dayjs(currentValues.PSRL_Rule_From_Date));
    const toDateChanged = !dayjs(originalValues.PSRL_Rule_To_Date).isSame(dayjs(currentValues.PSRL_Rule_To_Date));
    
    return fromDateChanged || toDateChanged;
  };

  const handleFormChange = () => {
    if (!isEdit) return;
    
    const currentValues = form.getFieldsValue();
    const changed = checkForChanges(currentValues);
    setHasChanges(changed);
  };

  const validateDateOverlap = async (agencyId, fromDate, toDate) => {
    if (!agencyId) return true;
    
    try {
      // Handle null dates - null means "forever"
      const fromDateStr = fromDate ? fromDate.format('YYYY-MM-DD') : null;
      const toDateStr = toDate ? toDate.format('YYYY-MM-DD') : null;
      
      const result = await checkOverlappingDates(
        agencyId, 
        fromDateStr, 
        toDateStr,
        isEdit ? parseInt(id) : null
      );
      return !result.hasOverlap;
    } catch (err) {
      console.error('Error checking date overlap:', err);
      return false;
    }
  };

  const validateTransfersOutsideDateRange = async (ruleId, fromDate, toDate) => {
    if (!isEdit || !ruleId) return { isValid: true, transfersOutsideRange: [] };
    
    try {
      // Handle null dates - null means "forever"
      const fromDateStr = fromDate ? fromDate.format('YYYY-MM-DD') : null;
      const toDateStr = toDate ? toDate.format('YYYY-MM-DD') : null;
      
      const result = await checkTransfersOutsideDateRange(
        ruleId,
        fromDateStr,
        toDateStr
      );
      
      return {
        isValid: !result.hasTransfersOutsideRange,
        transfersOutsideRange: result.transfersOutsideRange || []
      };
    } catch (err) {
      console.error('Error checking transfers outside date range:', err);
      return { isValid: false, transfersOutsideRange: [] };
    }
  };

  const showTransfersOutsideRangeModal = (transfersOutsideRange, fromDate, toDate) => {
    const formatDate = (date) => date ? dayjs(date).format('MMM DD, YYYY') : 'No limit';
    
    Modal.error({
      title: 'Cannot Update Date Range',
      width: 600,
      centered: true,
      content: (
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', fontSize: '14px', lineHeight: '1.5' }}>
            The new date range conflicts with existing transfers. The following {transfersOutsideRange.length} transfer{transfersOutsideRange.length !== 1 ? 's' : ''} would fall outside the new date range:
          </p>
          
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            fontSize: '13px'
          }}>
            <strong>New Date Range:</strong> {formatDate(fromDate)} to {formatDate(toDate)}
          </div>
          
          <div style={{ 
            maxHeight: '200px', 
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px'
          }}>
            {transfersOutsideRange.map((transfer, index) => (
              <div 
                key={transfer.transferId} 
                style={{ 
                  padding: '8px 12px', 
                  borderBottom: index < transfersOutsideRange.length - 1 ? '1px solid #f0f0f0' : 'none',
                  fontSize: '13px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    <strong>Transfer #{transfer.transferId}</strong> - {transfer.points} points
                  </span>
                  <span style={{ color: '#666' }}>
                    {dayjs(transfer.transferDate).format('MMM DD, YYYY HH:mm')}
                  </span>
                </div>
                {transfer.notes && (
                  <div style={{ color: '#666', marginTop: '4px' }}>
                    {transfer.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
            Please adjust the date range to include all existing transfers, or contact support if you need to modify these transfers.
          </p>
        </div>
      ),
    });
  };

  const handleCancel = () => {
    if (isEdit && hasChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        icon: (
          <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center' }}>
            <ExclamationCircleOutlined />
          </div>
        ),
        content: (
          <div style={{ padding: '16px 0', lineHeight: '1.5' }}>
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </div>
        ),
        centered: true,
        width: 420,
        bodyStyle: { 
          padding: '20px 24px',
          fontSize: '14px'
        },
        footer: (
          <div style={{ padding: '10px 24px 20px 24px', textAlign: 'right' }}>
            <Space>
              <Button onClick={() => Modal.destroyAll()}>
                Stay
              </Button>
              <Button 
                type="primary" 
                danger
                onClick={() => {
                  Modal.destroyAll();
                  navigate("/settings?tab=3&subtab=2");
                }}
              >
                Leave
              </Button>
            </Space>
          </div>
        ),
      });
    } else {
      navigate("/settings?tab=3&subtab=2");
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Only run validation for active rules or when changing from inactive to active
      if (values.PSRL_Active && values.PSRL_ForAgency_ACAY_KeyID) {
        // Use null dates as "forever" - null start means from beginning, null end means forever
        const fromDate = values.PSRL_Rule_From_Date || null;
        const toDate = values.PSRL_Rule_To_Date || null;
        
        const hasOverlap = !(await validateDateOverlap(
          values.PSRL_ForAgency_ACAY_KeyID,
          fromDate,
          toDate
        ));
        
        if (hasOverlap) {
          message.error("There are overlapping date ranges for this agency. Please choose different dates or set the rule to inactive.");
          return;
        }
      }

      // Validate transfers outside date range for edit mode - only for active rules
      if (isEdit && values.PSRL_Active && (values.PSRL_Rule_From_Date || values.PSRL_Rule_To_Date)) {
        const validation = await validateTransfersOutsideDateRange(
          parseInt(id),
          values.PSRL_Rule_From_Date,
          values.PSRL_Rule_To_Date
        );
        
        if (!validation.isValid) {
          showTransfersOutsideRangeModal(
            validation.transfersOutsideRange,
            values.PSRL_Rule_From_Date,
            values.PSRL_Rule_To_Date
          );
          return;
        }
      }
      
      // Prepare the data
      const ruleData = {
        PSRL_TransferType: values.PSRL_TransferType,
        PSRL_ForAgency_ACAY_KeyID: values.PSRL_ForAgency_ACAY_KeyID,
        PSRL_TransferToAgent_ACAG_KeyID: values.PSRL_TransferToAgent_ACAG_KeyID,
        PSRL_Rule_From_Date: values.PSRL_Rule_From_Date ? values.PSRL_Rule_From_Date.format('YYYY-MM-DD') : null,
        PSRL_Rule_To_Date: values.PSRL_Rule_To_Date ? values.PSRL_Rule_To_Date.format('YYYY-MM-DD') : null,
        PSRL_Active: values.PSRL_Active,
        PSRL_Rule_Name: values.PSRL_Rule_Name,
        PSRL_Note: values.PSRL_Note,
        PSRL_TransferPointsPercentage: values.PSRL_TransferPointsPercentage || null
      };

      if (isEdit) {
        ruleData.PSRL_KeyID = parseInt(id);
        await updateRule(ruleData);
        message.success("Rule updated successfully");
      } else {
        await createRule(ruleData);
        message.success("Rule created successfully");
      }
      
      // Navigate to Transfer Rules tab after creation/update
      navigate("/settings?tab=3&subtab=2");
    } catch (err) {
      message.error(err.message || `Failed to ${isEdit ? 'update' : 'create'} rule`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminAppLayout>
      <div
      className="page-content-wrapper"
      >
        <Breadcrumb
          items={[
            { title: "Settings" },
            { title: isEdit ? "Edit Transfer Rule" : "New Transfer Rule" },
          ]}
        />
        <Title level={4} style={{ color: colors.black, marginTop: 3 }}>
          {isEdit ? 'Edit Transfer Rule' : 'Create New Transfer Rule'}
        </Title>
        <Divider type="horizontal" />
        
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
          >
            Back to Rules
          </Button>
        </Space>

        <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          initialValues={{
            PSRL_TransferType: "Agency_to_Agent",
            PSRL_Active: true,
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Transfer Type"
                name="PSRL_TransferType"
                rules={[{ required: true, message: "Please select transfer type" }]}
              >
                <Select disabled>
                  <Option value="Agency_to_Agent">Agency to Agent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="PSRL_Active"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Rule Name"
                name="PSRL_Rule_Name"
                rules={[{ required: true, message: 'Please enter a rule name' }]}
              >
                <Input 
                  placeholder="Enter a descriptive name for this rule"
                  maxLength={255}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Rule Notes"
                name="PSRL_Note"
                extra="Optional notes or description for this rule"
              >
                <TextArea 
                  placeholder="Enter any additional notes or description for this rule"
                  rows={3}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="From Agent/s"
                name="PSRL_ForAgency_ACAY_KeyID"
                rules={[{ required: true, message: "Please select an agency" }]}
              >
                <Select
                  placeholder="Select Agency"
                  loading={searchingAgencies}
                  showSearch
                  disabled={isEdit}
                  onChange={handleAgencyChange}
                  onSearch={debouncedAgencySearch}
                  filterOption={false} // Disable client-side filtering since we're using server-side search
                  notFoundContent={searchingAgencies ? "Loading agencies..." : "Type to search agencies"}
                >
                  {agencies.map(agency => (
                    <Option key={agency.ACAY_KeyID} value={agency.ACAY_KeyID}>
                      {agency.ACAY_AgencyName} {agency.ACAY_AliasCode && `(${agency.ACAY_AliasCode})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="To Agent"
                name="PSRL_TransferToAgent_ACAG_KeyID"
                rules={[{ required: true, message: "Please select an agent" }]}
              >
                <Select
                  placeholder={selectedAgency ? "Select Agent" : "Select an agency first"}
                  loading={searchingAgents}
                  disabled={!selectedAgency || isEdit}
                  showSearch
                  notFoundContent={searchingAgents ? "Loading agents..." : "Type to search agents"}
                  onSearch={debouncedAgentSearch}
                  filterOption={false} // Disable client-side filtering since we're using server-side search
                >
                  {filteredAgents.map(agent => (
                    <Option key={agent.ACAG_KeyID} value={agent.ACAG_KeyID}>
                      {agent.ACAG_Name} ({agent.ACAG_Email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Rule Start Date"
                name="PSRL_Rule_From_Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Rule End Date"
                name="PSRL_Rule_To_Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Submission Sharing Percentage"
                name="PSRL_TransferPointsPercentage"
                extra={isEdit ? 
                  "Percentage cannot be modified after rule creation to maintain data integrity." : 
                  "Optional: Percentage of submission points to share with the target agent (0.01-100.00%). Leave empty for full agency-to-agent transfers."
                }
              >
                <InputNumber
                  placeholder="Enter percentage (0.01-100.00)"
                  style={{ width: '100%' }}
                  min={0.01}
                  max={100.00}
                  step={0.01}
                  precision={2}
                  addonAfter="%"
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button 
                type="primary"
                htmlType="submit" 
                loading={loading}
                disabled={isEdit && !hasChanges}
                icon={<SaveOutlined />}
              >
                {isEdit ? 'Update Rule' : 'Create Rule'}
              </Button>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
        </Card>
        
        {/* Transfers Table - Only show for edit mode */}
        {isEdit && (
          <div style={{ marginTop: 24 }}>
            <TransfersTable 
              transfers={transfers}
              loading={transfersLoading}
              title="Associated Transfers"
            />
          </div>
        )}

        {/* Affected Submissions Table - Only show for edit mode */}
        {isEdit && (
          <div style={{ marginTop: 24 }}>
            <Card 
              title="Affected Submissions" 
              loading={submissionsLoading}
            >
              {affectedSubmissions.length > 0 ? (
                <Table
                  dataSource={affectedSubmissions}
                  columns={[
                    {
                      title: "Policy Name",
                      dataIndex: "PolicyName",
                      key: "PolicyName",
                      ellipsis: true,
                    },
                    {
                      title: "Policy Date",
                      dataIndex: "PolicyDate",
                      key: "PolicyDate",
                      render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A',
                      width: 120,
                    },
                    {
                      title: "Points",
                      dataIndex: "Points",
                      key: "Points",
                      render: (points) => <Tag color="blue">{points} pts</Tag>,
                      width: 80,
                    },
                    {
                      title: "Writing Agent",
                      dataIndex: "AgentName",
                      key: "AgentName",
                      ellipsis: true,
                    },
                    {
                      title: "Shareholder Agent",
                      dataIndex: "ShareholderAgentName",
                      key: "ShareholderAgentName",
                      ellipsis: true,
                    },
                    {
                      title: "Percentage",
                      dataIndex: "ShareholderPercentage",
                      key: "ShareholderPercentage",
                      render: (percentage) => percentage ? `${percentage}%` : 'N/A',
                      width: 100,
                    },
                    {
                      title: "Effective Date",
                      dataIndex: "EffectiveDate",
                      key: "EffectiveDate",
                      render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : 'N/A',
                      width: 120,
                    },
                  ]}
                  rowKey="SubmissionID"
                  pagination={false}
                  size="small"
                  scroll={{ x: 800 }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  <Text>No submissions have been affected by this rule yet</Text>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </AdminAppLayout>
  );
};

export default PSRLRuleForm;
